import { createContext, useContext, type ReactNode } from "react";
import { useGetAuthUser, useLogin, useLogout, useRegister, getGetAuthUserQueryKey, type AuthUser } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: ReturnType<typeof useLogin>["mutateAsync"];
  isLoggingIn: boolean;
  loginError: Error | null;
  register: ReturnType<typeof useRegister>["mutateAsync"];
  isRegistering: boolean;
  registerError: Error | null;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const authQueryKey = getGetAuthUserQueryKey();

  const { data: user, isLoading, error } = useGetAuthUser({
    query: {
      queryKey: authQueryKey,
      retry: false,
      staleTime: 5 * 60 * 1000,
    },
  });

  const loginMutation = useLogin({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: authQueryKey });
      },
    },
  });

  const registerMutation = useRegister({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: authQueryKey });
      },
    },
  });

  const logoutMutation = useLogout({
    mutation: {
      onSuccess: () => {
        queryClient.setQueryData(authQueryKey, null);
        queryClient.clear();
        window.location.replace("/inloggen");
      },
    },
  });

  const value: AuthContextValue = {
    user: error ? null : (user ?? null),
    isAuthenticated: !!user && !error,
    isLoading,
    login: loginMutation.mutateAsync,
    isLoggingIn: loginMutation.isPending,
    loginError: loginMutation.error,
    register: registerMutation.mutateAsync,
    isRegistering: registerMutation.isPending,
    registerError: registerMutation.error,
    logout: () => logoutMutation.mutate(),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
}
